import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import PropertyImage from '../components/PropertyImage';
import { 
  LogOut, 
  Users, 
  CheckCircle, 
  Clock, 
  XCircle,
  Mail,
  Phone,
  Euro,
  Calendar,
  ExternalLink,
  Image as ImageIcon,
  Home as HomeIcon,
  MessageSquare,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const fetchData = async () => {
      try {
        const [submissionsRes, statsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/submissions`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${BACKEND_URL}/api/admin/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        setSubmissions(submissionsRes.data.submissions || []);
        setStats(statsRes.data);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('adminToken');
          navigate('/admin/login');
        } else {
          setError('Errore nel caricamento dei dati');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-700 border-yellow-200', text: 'In Attesa' },
      approved: { icon: CheckCircle, color: 'bg-green-100 text-green-700 border-green-200', text: 'Approvato' },
      rejected: { icon: XCircle, color: 'bg-red-100 text-red-700 border-red-200', text: 'Rifiutato' }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${badge.color}`}>
        <Icon className="w-4 h-4" />
        {badge.text}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-600">Caricamento...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Dashboard Admin</h1>
              <p className="text-slate-600">Gestione Richieste Proprietari Fondatori</p>
            </div>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Link to="/admin/properties">
            <Card className="p-6 hover:shadow-xl transition-all cursor-pointer border-2 border-transparent hover:border-emerald-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <HomeIcon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Gestione Immobili</h3>
                    <p className="text-sm text-slate-600">Crea e modifica annunci</p>
                  </div>
                </div>
                <ArrowRight className="w-6 h-6 text-slate-400" />
              </div>
            </Card>
          </Link>

          <Link to="/admin/contacts">
            <Card className="p-6 hover:shadow-xl transition-all cursor-pointer border-2 border-transparent hover:border-blue-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Richieste di Contatto</h3>
                    <p className="text-sm text-slate-600">Gestisci le richieste inquilini</p>
                  </div>
                </div>
                <ArrowRight className="w-6 h-6 text-slate-400" />
              </div>
            </Card>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white border-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm mb-1">Totale Richieste</p>
                <p className="text-4xl font-bold">{stats.total}</p>
              </div>
              <Users className="w-10 h-10 text-blue-100" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-yellow-500 to-yellow-600 text-white border-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm mb-1">In Attesa</p>
                <p className="text-4xl font-bold">{stats.pending}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-100" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white border-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm mb-1">Approvati</p>
                <p className="text-4xl font-bold">{stats.approved}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-100" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-red-500 to-red-600 text-white border-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm mb-1">Rifiutati</p>
                <p className="text-4xl font-bold">{stats.rejected}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-100" />
            </div>
          </Card>
        </div>

        {/* Submissions Table */}
        <Card className="p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Richieste Recenti</h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {submissions.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Nessuna richiesta ancora</p>
            </div>
          ) : (
            <div className="space-y-4">
              {submissions.map((submission) => (
                <Card key={submission.id} className="p-6 border-slate-200 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{submission.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(submission.created_at)}
                      </div>
                    </div>
                    {getStatusBadge(submission.status)}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Email</p>
                        <a href={`mailto:${submission.email}`} className="text-sm text-blue-600 hover:underline">
                          {submission.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Telefono</p>
                        <a href={`tel:${submission.phone}`} className="text-sm text-slate-900">
                          {submission.phone}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Euro className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Canone Mensile</p>
                        <p className="text-sm font-semibold text-slate-900">€{submission.monthly_rent}</p>
                      </div>
                    </div>

                    {submission.listing_link && (
                      <div className="flex items-center gap-3">
                        <ExternalLink className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">Link Annuncio</p>
                          <a 
                            href={submission.listing_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline"
                          >
                            Vedi annuncio
                          </a>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-500 mb-2">Descrizione Immobile</p>
                    <p className="text-sm text-slate-700 leading-relaxed">{submission.description}</p>
                  </div>

                  {/* Images Section */}
                  {submission.images && submission.images.length > 0 ? (
                    <div className="pt-4 border-t border-slate-200">
                      <div className="flex items-center gap-2 mb-3">
                        <ImageIcon className="w-4 h-4 text-emerald-600" />
                        <p className="text-xs text-slate-500 font-semibold">
                          Foto Allegate ({submission.images.length})
                        </p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {submission.images.map((image, idx) => {
                          const imageUrl = `${BACKEND_URL}/api/admin/property-image/${submission.id}/${image}`;
                          
                          return (
                            <div key={idx} className="group relative">
                              <div 
                                className="w-full h-32 bg-gradient-to-br from-emerald-50 to-blue-50 border-2 border-emerald-200 rounded-lg overflow-hidden relative group-hover:border-emerald-500 transition-colors cursor-pointer"
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('adminToken');
                                    const response = await axios.get(imageUrl, {
                                      headers: {
                                        'Authorization': `Bearer ${token}`
                                      },
                                      responseType: 'blob'
                                    });
                                    
                                    const url = URL.createObjectURL(response.data);
                                    const newWindow = window.open('', '_blank', 'width=800,height=600');
                                    newWindow.document.write(`
                                      <html>
                                        <head>
                                          <title>Foto ${idx + 1}</title>
                                          <style>
                                            body { margin: 0; padding: 0; background: #000; display: flex; justify-content: center; align-items: center; height: 100vh; }
                                            img { max-width: 100%; max-height: 100vh; object-fit: contain; }
                                          </style>
                                        </head>
                                        <body>
                                          <img src="${url}" alt="Foto ${idx + 1}" />
                                        </body>
                                      </html>
                                    `);
                                  } catch (error) {
                                    console.error('Error loading image:', error);
                                    alert('Errore nel caricamento della foto');
                                  }
                                }}
                              >
                                <PropertyImage imageUrl={imageUrl} idx={idx} />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                  <ExternalLink className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                                <div className="absolute bottom-1 right-1 bg-emerald-600 text-white text-xs px-2 py-1 rounded font-semibold">
                                  {idx + 1}
                                </div>
                              </div>
                              <p className="text-xs text-slate-600 mt-1 truncate group-hover:text-emerald-600" title={image}>
                                📷 Foto {idx + 1}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-slate-500 mt-2 italic">
                        💡 Click sulle foto per ingrandirle
                      </p>
                    </div>
                  ) : (
                    <div className="pt-4 border-t border-slate-200">
                      <p className="text-xs text-slate-500 italic">
                        📷 Nessuna foto allegata
                      </p>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
